import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    PiArrowLeftBold,
    PiArrowRightBold,
    PiCheckCircleDuotone,
    PiClipboardTextDuotone,
    PiCoinsDuotone,
    PiListChecksDuotone,
    PiSpinnerGapBold,
    PiWarningCircleDuotone,
} from 'react-icons/pi';

import FeedbackModal from 'components/FeedbackModal';
import PageHeading from 'elements/PageHeading';
import Pattern from 'elements/Pattern';
import { useAuth } from 'providers/AuthProvider';
import { getSurvey, getSurveyList, submitSurvey } from 'services/surveyService';
import { getColorByIndex, getColorConfig } from 'utils/StyleUtils';

const QUESTION_TYPES = {
    STRING: 'STRING',
    TEXT: 'TEXT',
    DROPDOWN: 'DROPDOWN',
    SELECT_ONE: 'SELECT_ONE',
    SELECT_ANY: 'SELECT_ANY',
    SCALE: 'SCALE',
};

const getQuestionType = (question) => {
    const rawType = question?.questionType;

    if (typeof rawType === 'string') return rawType.toUpperCase();
    if (typeof rawType?.name === 'string') return rawType.name.toUpperCase();
    if (typeof rawType?.value === 'string') return rawType.value.toUpperCase();

    return '';
};

const getQuestionList = (survey) => {
    if (Array.isArray(survey?.questionList)) return survey.questionList;
    return survey?.question ? [survey.question] : [];
};

const normalizeSurvey = (survey) => {
    if (!survey || typeof survey !== 'object') return null;

    const parsedIndex = Number(survey.questionIndex);

    return {
        ...survey,
        questionIndex: Number.isInteger(parsedIndex) ? parsedIndex : 0,
        questionList: getQuestionList(survey),
        hasNextQuestion: Boolean(survey.hasNextQuestion),
    };
};

const getInitialAnswers = (survey) => {
    const answers = {};

    getQuestionList(survey).forEach((question) => {
        const savedAnswer = survey?.answers?.[question.key] ?? survey?.answer ?? null;
        answers[question.key] = savedAnswer === null || savedAnswer === undefined
            ? null
            : String(savedAnswer);
    });

    return answers;
};

const getValidationMessages = (validationResult) => {
    const messages = Array.isArray(validationResult?.errorMessages)
        ? validationResult.errorMessages.filter(Boolean)
        : [];

    Object.values(validationResult?.properties || {}).forEach((items) => {
        if (!Array.isArray(items)) return;

        items.forEach((item) => {
            const message = item?.message || [item?.topic, item?.key].filter(Boolean).join(' – ');
            if (message) messages.push(message);
        });
    });

    return [...new Set(messages)];
};

export default function SatisfactionMeasurementPage({ className = '' }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const surveyKey = searchParams.get('key');
    const { userData, modifyUserDataValue } = useAuth();
    const isMemberRoute = location.pathname === '/diak-bejelentkezett-elegedettsegmeres';

    const [surveyList, setSurveyList] = useState([]);
    const [survey, setSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [validationMessages, setValidationMessages] = useState([]);
    const [feedback, setFeedback] = useState(null);

    const loadContent = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        setValidationMessages([]);

        try {
            if (surveyKey) {
                const response = await getSurvey(surveyKey, undefined, signal);
                const nextSurvey = normalizeSurvey(response);

                if (!nextSurvey) {
                    throw new Error('SURVEY_NOT_FOUND');
                }

                setSurvey(nextSurvey);
                setSurveyList([]);
            } else {
                const response = await getSurveyList(signal);
                const list = Array.isArray(response)
                    ? response
                    : Array.isArray(response?.data) ? response.data : [];

                setSurveyList(list);
                setSurvey(null);
            }
        } catch (requestError) {
            if (requestError.code === 'ERR_CANCELED') return;

            console.error('Survey loading failed:', requestError);
            setError(
                requestError.message === 'SURVEY_NOT_FOUND'
                    ? 'A kérdőív nem található, vagy már nem érhető el.'
                    : 'Az elégedettségmérő kérdőíveket jelenleg nem sikerült betölteni.'
            );
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [surveyKey]);

    useEffect(() => {
        const controller = new AbortController();
        loadContent(controller.signal);

        return () => controller.abort();
    }, [loadContent]);

    useEffect(() => {
        if (!survey) {
            setAnswers({});
            return;
        }

        setAnswers(getInitialAnswers(survey));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [survey]);

    const openSurvey = (key) => {
        setSearchParams({ key });
    };

    const leaveSurvey = useCallback(() => {
        setFeedback(null);

        if (userData || isMemberRoute) {
            setSearchParams({}, { replace: true });
        } else {
            navigate('/', { replace: true });
        }
    }, [isMemberRoute, navigate, setSearchParams, userData]);

    const applyNextSurvey = useCallback((nextSurvey) => {
        const normalizedNext = normalizeSurvey(nextSurvey);

        if (!normalizedNext) {
            leaveSurvey();
            return;
        }

        if (normalizedNext.key === survey?.key) {
            setSurvey(normalizedNext);
        } else {
            setSearchParams({ key: normalizedNext.key });
        }
    }, [leaveSurvey, setSearchParams, survey?.key]);

    const addRewardToDisplayedBalance = (shopScore) => {
        if (!userData || shopScore === null || shopScore === undefined) return;

        const reward = Number(shopScore);
        if (!Number.isFinite(reward)) return;

        modifyUserDataValue('credit', Number(userData.credit || 0) + reward);
    };

    const handleSubmit = async () => {
        if (!survey || submitting) return;

        setSubmitting(true);
        setError(null);
        setValidationMessages([]);

        try {
            const result = await submitSurvey({
                surveyKey: survey.key,
                questionIndex: survey.questionIndex,
                answers: survey.questionList.reduce((collectedAnswers, question) => {
                    collectedAnswers[question.key] = answers[question.key] ?? null;
                    return collectedAnswers;
                }, {}),
            });

            if (!result?.successful) {
                const messages = getValidationMessages(result);
                setValidationMessages(
                    messages.length
                        ? messages
                        : ['Sikertelen küldés. Kérjük, ellenőrizd a megadott válaszokat!']
                );
                return;
            }

            const nextSurvey = result.next || null;
            const reward = nextSurvey
                ? result.shopScore
                : (result.shopScore ?? survey.shopScore);

            if (reward !== null && reward !== undefined) {
                addRewardToDisplayedBalance(reward);
            }

            if (nextSurvey && reward === null) {
                applyNextSurvey(nextSurvey);
                return;
            }

            if (nextSurvey && reward === undefined) {
                applyNextSurvey(nextSurvey);
                return;
            }

            setFeedback({
                reward,
                nextSurvey,
                final: !nextSurvey,
            });
        } catch (requestError) {
            console.error('Survey submission failed:', requestError);
            setError('A válaszokat nem sikerült elküldeni. Kérjük, próbáld újra később.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = async () => {
        if (!survey || submitting) return;

        if (survey.questionIndex <= 0) {
            leaveSurvey();
            return;
        }

        setLoading(true);
        setError(null);
        setValidationMessages([]);

        try {
            const response = await getSurvey(survey.key, survey.questionIndex - 1);
            const previousSurvey = normalizeSurvey(response);

            if (!previousSurvey) throw new Error('SURVEY_NOT_FOUND');
            setSurvey(previousSurvey);
        } catch (requestError) {
            console.error('Previous survey step loading failed:', requestError);
            setError('Az előző kérdés betöltése nem sikerült. Kérjük, próbáld újra.');
        } finally {
            setLoading(false);
        }
    };

    const confirmFeedback = () => {
        const nextSurvey = feedback?.nextSurvey;
        const isFinal = feedback?.final;
        setFeedback(null);

        if (nextSurvey) {
            applyNextSurvey(nextSurvey);
        } else if (isFinal) {
            leaveSurvey();
        }
    };

    const headingTitle = survey?.title || 'Elégedettségmérés';
    const headingSubtitle = survey
        ? survey.description && <RichText html={survey.description} />
        : (
            <span className="block max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
                Kíváncsiak vagyunk a véleményedre. Töltsd ki elérhető kérdőíveinket, és segíts abban,
                hogy szolgáltatásainkat a diákok igényeihez igazíthassuk.
            </span>
        );

    return (
        <div className={`relative flex w-full flex-1 flex-col items-center overflow-hidden gradient-yellow px-site py-site ${className}`}>
            <Pattern className="pointer-events-none opacity-55" />

            <main className="relative z-10 flex w-full max-w-boxed flex-col gap-8 md:gap-10">
                <PageHeading
                    title={headingTitle}
                    subTitle={headingSubtitle}
                    align="left"
                    mobileAlign="center"
                />

                {error && (
                    <AlertMessage Icon={PiWarningCircleDuotone} tone="error">
                        {error}
                    </AlertMessage>
                )}

                {loading ? (
                    <LoadingCard />
                ) : survey ? (
                    <SurveyForm
                        survey={survey}
                        answers={answers}
                        validationMessages={validationMessages}
                        submitting={submitting}
                        onAnswerChange={(questionKey, answer) => {
                            setAnswers((current) => ({ ...current, [questionKey]: answer }));
                            setValidationMessages([]);
                        }}
                        onBack={handleBack}
                        onSubmit={handleSubmit}
                    />
                ) : (
                    <SurveyList
                        items={surveyList}
                        userData={userData}
                        onOpen={openSurvey}
                    />
                )}
            </main>

            <SurveyFeedbackModal
                feedback={feedback}
                onConfirm={confirmFeedback}
            />
        </div>
    );
}

function SurveyList({ items, userData, onOpen }) {
    if (!items.length) {
        return (
            <div className="mx-auto flex w-full max-w-3xl flex-col items-center rounded-3xl bg-white p-8 text-center text-neutral-800 shadow-2xl sm:p-12">
                <span className="mb-5 flex size-16 items-center justify-center rounded-2xl bg-neutral-100 text-neutral-400">
                    <PiClipboardTextDuotone className="size-9" />
                </span>
                <h2 className="font-display text-2xl font-black">Jelenleg nincs elérhető kérdőív</h2>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-neutral-500">
                    Köszönjük, hogy segítenél nekünk. Ha új kérdőív válik elérhetővé, ezen az oldalon fog megjelenni.
                </p>
                {!userData && (
                    <Link
                        to="/bejelentkezes"
                        state={{ returnTo: '/diak-bejelentkezett-elegedettsegmeres' }}
                        className="gradient-blue mt-6 inline-flex items-center justify-center rounded-xl px-6 py-3 font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
                    >
                        Belépés a saját kérdőívekhez
                    </Link>
                )}
            </div>
        );
    }

    return (
        <section className="grid gap-5 md:grid-cols-2">
            {items.map((item, index) => (
                <SurveyListItem
                    key={item.key || item.id || index}
                    item={item}
                    index={index}
                    onOpen={() => onOpen(item.key)}
                />
            ))}
        </section>
    );
}

function SurveyListItem({ item, index, onOpen }) {
    const color = getColorByIndex(Number(item.id ?? index));
    const colorConfig = getColorConfig(color) || getColorConfig('blue');
    const hasReward = item.shopScore !== null && item.shopScore !== undefined;

    return (
        <article className="flex min-h-64 flex-col overflow-hidden rounded-3xl bg-white text-neutral-900 shadow-2xl transition hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(0,0,0,0.2)]">
            <div className="flex flex-1 flex-col p-6 sm:p-7">
                <div className="flex items-start justify-between gap-4">
                    <span className={`flex size-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg ${colorConfig.gradient}`}>
                        <PiListChecksDuotone className="size-7" />
                    </span>
                    {hasReward && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1.5 text-xs font-black text-white">
                            <PiCoinsDuotone className={colorConfig.text} /> +{item.shopScore} pont
                        </span>
                    )}
                </div>

                <h2 className="mt-5 font-display text-2xl font-black leading-tight text-neutral-900">{item.title}</h2>
                {item.description && (
                    <div className="mt-3 line-clamp-4 text-sm leading-relaxed text-neutral-600">
                        <RichText html={item.description} />
                    </div>
                )}

                <button
                    type="button"
                    onClick={onOpen}
                    className={`mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110 ${colorConfig.gradient}`}
                >
                    Kitöltöm <PiArrowRightBold />
                </button>
            </div>
        </article>
    );
}

function SurveyForm({ survey, answers, validationMessages, submitting, onAnswerChange, onBack, onSubmit }) {
    const color = getColorByIndex(Number(survey.id ?? 0));
    const colorConfig = getColorConfig(color) || getColorConfig('blue');
    const hasReward = survey.shopScore !== null && survey.shopScore !== undefined;

    return (
        <section className="mx-auto w-full max-w-4xl overflow-hidden rounded-3xl bg-white text-neutral-900 shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-neutral-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <div className="flex items-center gap-3">
                    <span className={`flex size-11 items-center justify-center rounded-xl text-white ${colorConfig.gradient}`}>
                        <PiClipboardTextDuotone className="size-6" />
                    </span>
                    <div>
                        <span className="block text-xs font-bold uppercase tracking-wider text-neutral-400">Kérdőív kitöltése</span>
                        <strong className="font-display text-lg font-black">{survey.questionIndex + 1}. lépés</strong>
                    </div>
                </div>

                {hasReward && (
                    <span className="inline-flex w-fit items-center gap-2 rounded-xl bg-neutral-900 px-3.5 py-2 text-sm font-black text-white">
                        <PiCoinsDuotone className={`size-5 ${colorConfig.text}`} />
                        +{survey.shopScore} pont a kitöltésért
                    </span>
                )}
            </div>

            <div className="space-y-5 p-5 sm:p-7">
                {!survey.questionList.length && (
                    <AlertMessage Icon={PiWarningCircleDuotone} tone="warning">
                        Ez a kérdőív nem tartalmaz megjeleníthető kérdést.
                    </AlertMessage>
                )}

                {survey.questionList.map((question, index) => (
                    <SurveyQuestion
                        key={question.key || index}
                        question={question}
                        value={answers[question.key] ?? null}
                        colorConfig={colorConfig}
                        onChange={(answer) => onAnswerChange(question.key, answer)}
                    />
                ))}

                {!!validationMessages.length && (
                    <AlertMessage Icon={PiWarningCircleDuotone} tone="error">
                        <ul className="space-y-1">
                            {validationMessages.map((message) => <li key={message}>{message}</li>)}
                        </ul>
                    </AlertMessage>
                )}
            </div>

            <div className="grid gap-3 border-t border-neutral-100 bg-neutral-50 p-4 sm:grid-cols-2 sm:p-5">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={submitting}
                    className="order-2 flex items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-5 py-3.5 font-black text-neutral-700 transition hover:border-neutral-300 hover:bg-neutral-100 disabled:opacity-50 sm:order-1"
                >
                    <PiArrowLeftBold /> {survey.questionIndex > 0 ? 'Vissza' : 'Mégsem'}
                </button>
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={submitting || !survey.questionList.length}
                    className={`order-1 flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:order-2 ${colorConfig.gradient}`}
                >
                    {submitting ? (
                        <><PiSpinnerGapBold className="animate-spin" /> Küldés…</>
                    ) : survey.hasNextQuestion ? (
                        <>Tovább <PiArrowRightBold /></>
                    ) : (
                        <>Elküldöm <PiCheckCircleDuotone className="size-5" /></>
                    )}
                </button>
            </div>
        </section>
    );
}

function SurveyQuestion({ question, value, colorConfig, onChange }) {
    const type = getQuestionType(question);

    return (
        <fieldset className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4 sm:p-5">
            <legend className="w-full px-0 pb-3 font-display text-lg font-black leading-snug text-neutral-900 sm:text-xl">
                <RichText html={question.question || ''} />
            </legend>

            <QuestionInput
                type={type}
                question={question}
                value={value}
                colorConfig={colorConfig}
                onChange={onChange}
            />
        </fieldset>
    );
}

function QuestionInput({ type, question, value, colorConfig, onChange }) {
    const inputClassName = 'w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-green-500 focus:ring-4 focus:ring-green-100';

    if (type === QUESTION_TYPES.STRING) {
        return (
            <input
                type="text"
                value={value ?? ''}
                onChange={(event) => onChange(event.target.value)}
                className={inputClassName}
            />
        );
    }

    if (type === QUESTION_TYPES.TEXT) {
        return (
            <textarea
                rows={6}
                value={value ?? ''}
                onChange={(event) => onChange(event.target.value)}
                className={`${inputClassName} min-h-36 resize-y`}
            />
        );
    }

    if (type === QUESTION_TYPES.DROPDOWN) {
        return (
            <select
                value={value ?? ''}
                onChange={(event) => onChange(event.target.value || null)}
                className={inputClassName}
            >
                <option value="">Kérjük, válassz…</option>
                {(question.answerList || []).map((answer) => (
                    <option key={answer.key} value={answer.key}>{answer.answer}</option>
                ))}
            </select>
        );
    }

    if (type === QUESTION_TYPES.SCALE) {
        return (
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
                {[1, 2, 3, 4, 5].map((scaleValue) => {
                    const checked = String(value) === String(scaleValue);
                    return (
                        <button
                            key={scaleValue}
                            type="button"
                            aria-pressed={checked}
                            onClick={() => onChange(String(scaleValue))}
                            className={`flex aspect-square items-center justify-center rounded-xl border font-display text-xl font-black transition sm:aspect-auto sm:h-14 ${checked ? `${colorConfig.gradient} border-transparent text-white shadow-lg` : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-100'}`}
                        >
                            {scaleValue}
                        </button>
                    );
                })}
            </div>
        );
    }

    if (type === QUESTION_TYPES.SELECT_ONE || type === QUESTION_TYPES.SELECT_ANY) {
        const selectedValues = type === QUESTION_TYPES.SELECT_ANY
            ? new Set(String(value || '').split(',').filter(Boolean))
            : new Set(value ? [String(value)] : []);

        const toggleValue = (answerKey) => {
            if (type === QUESTION_TYPES.SELECT_ONE) {
                onChange(String(answerKey));
                return;
            }

            const nextValues = new Set(selectedValues);
            if (nextValues.has(String(answerKey))) nextValues.delete(String(answerKey));
            else nextValues.add(String(answerKey));

            const orderedValues = (question.answerList || [])
                .map((answer) => String(answer.key))
                .filter((key) => nextValues.has(key));
            onChange(orderedValues.length ? orderedValues.join(',') : null);
        };

        return (
            <div className="grid gap-2.5">
                {(question.answerList || []).map((answer) => {
                    const checked = selectedValues.has(String(answer.key));
                    return (
                        <button
                            key={answer.key}
                            type="button"
                            aria-pressed={checked}
                            onClick={() => toggleValue(answer.key)}
                            className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left text-sm font-semibold transition ${checked ? `${colorConfig.border} bg-white ring-4 ring-neutral-100` : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-100'}`}
                        >
                            <span className={`flex size-6 shrink-0 items-center justify-center border-2 transition ${type === QUESTION_TYPES.SELECT_ANY ? 'rounded-md' : 'rounded-full'} ${checked ? `${colorConfig.gradient} border-transparent text-white` : 'border-neutral-300 bg-white'}`}>
                                {checked && <PiCheckCircleDuotone className="size-4" />}
                            </span>
                            <span>{answer.answer}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <AlertMessage Icon={PiWarningCircleDuotone} tone="warning">
            Ez a kérdéstípus jelenleg nem támogatott ({type || 'ismeretlen típus'}).
        </AlertMessage>
    );
}

function SurveyFeedbackModal({ feedback, onConfirm }) {
    const isOpen = Boolean(feedback);

    return (
        <FeedbackModal
            open={isOpen}
            onClose={onConfirm}
            tone="success"
            title="Sikeres beküldés!"
            message="Köszönjük, hogy kitöltötted kérdőívünket!"
            actionLabel={feedback?.nextSurvey ? 'Tovább' : 'Köszönöm'}
        >
            {feedback?.reward !== null && feedback?.reward !== undefined && (
                <div className="mt-4 flex w-fit items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 font-black text-amber-700">
                    <PiCoinsDuotone className="size-6" />
                    +{feedback.reward} pont jóváírva
                </div>
            )}
        </FeedbackModal>
    );
}

function AlertMessage({ Icon, tone, children }) {
    const toneClassName = tone === 'error'
        ? 'border-red-200 bg-red-50 text-red-700'
        : 'border-amber-200 bg-amber-50 text-amber-800';

    return (
        <div className={`flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-sm font-semibold ${toneClassName}`}>
            <Icon className="mt-0.5 size-5 shrink-0" />
            <div>{children}</div>
        </div>
    );
}

function LoadingCard() {
    return (
        <div className="mx-auto flex w-full max-w-4xl flex-col items-center rounded-3xl bg-white/15 p-12 text-center text-white backdrop-blur-sm">
            <PiSpinnerGapBold className="size-10 animate-spin" />
            <p className="mt-4 font-display text-xl font-black">Kérdőívek betöltése…</p>
        </div>
    );
}

function RichText({ html }) {
    const content = useMemo(() => ({ __html: html || '' }), [html]);
    return <span dangerouslySetInnerHTML={content} />;
}
